import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getPublicSetting, getShopItems, getUserInventoryItemIds, purchaseShopItem, updateUserProfile } from '../api/supabaseAPI';
import useUser from '../hooks/useUser';
import '../styles/Layout.css';
import '../styles/Introduction.css';
import '../styles/Shop.css';

const FALLBACK_BASE_URL = (import.meta.env.VITE_R2_PUBLIC_BASE_URL || '').replace(/\/$/, '');

const formatTypeLabel = (value) => {
  if (!value) return 'Uncategorized';
  return value
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const buildAssetUrl = (baseUrl, path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  if (!baseUrl) return path;
  return `${baseUrl}/${String(path).replace(/^\/+/, '')}`;
};

export default function Shop() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data: userData } = useUser();
  const coins = Number(userData?.current_coins) || 0;
  const wallpaperItemId = userData?.wallpaper_item_id ?? null;
  const [previewItem, setPreviewItem] = useState(null);
  const [purchaseError, setPurchaseError] = useState('');
  const [applyError, setApplyError] = useState('');

  const { data: shopItems = [], isLoading } = useQuery({
    queryKey: ['shop_items'],
    queryFn: getShopItems,
    staleTime: 1000 * 60 * 5,
  });

  const { data: r2PublicBaseUrl } = useQuery({
    queryKey: ['public_settings', 'r2_public_base_url'],
    queryFn: () => getPublicSetting('r2_public_base_url'),
    staleTime: 1000 * 60 * 30,
    refetchOnMount: false,
  });

  const { data: ownedItemIds = [] } = useQuery({
    queryKey: ['shop_inventory_item_ids'],
    queryFn: getUserInventoryItemIds,
    staleTime: 1000 * 60,
  });

  const baseUrl = useMemo(() => {
    const rawBaseUrl = FALLBACK_BASE_URL || r2PublicBaseUrl || '';
    return rawBaseUrl.replace(/\/$/, '');
  }, [r2PublicBaseUrl]);

  const ownedItemIdSet = useMemo(() => new Set(ownedItemIds), [ownedItemIds]);

  const itemTypes = useMemo(() => {
    return Array.from(new Set(shopItems.map((item) => item.type).filter(Boolean)));
  }, [shopItems]);

  const itemsByType = useMemo(() => {
    return itemTypes.map((type) => ({
      type,
      label: formatTypeLabel(type),
      items: shopItems.filter((item) => item.type === type),
    }));
  }, [itemTypes, shopItems]);

  const equippedItemLabel = useMemo(() => {
    if (wallpaperItemId === null || wallpaperItemId === undefined) return t('shopPage.none');

    const equippedItem = shopItems.find((item) => Number(item.id) === Number(wallpaperItemId));
    if (!equippedItem) return String(wallpaperItemId);

    return equippedItem.name || equippedItem.code || t('shopPage.itemFallback', { id: equippedItem.id });
  }, [shopItems, t, wallpaperItemId]);

  useEffect(() => {
    if (!previewItem) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setPreviewItem(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [previewItem]);

  const purchaseMutation = useMutation({
    mutationFn: purchaseShopItem,
    onMutate: () => {
      setPurchaseError('');
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['shop_inventory_item_ids'] }),
        queryClient.invalidateQueries({ queryKey: ['user'] }),
      ]);
    },
    onError: (error) => {
      setPurchaseError(error?.message || t('shopPage.purchaseFailed'));
    },
  });

  const applyMutation = useMutation({
    mutationFn: ({ userId, wallpaperItemId: nextWallpaperItemId }) =>
      updateUserProfile(userId, { wallpaper_item_id: nextWallpaperItemId }),
    onMutate: async ({ wallpaperItemId: nextWallpaperItemId }) => {
      setApplyError('');
      await queryClient.cancelQueries({ queryKey: ['user', userData?.id] });

      const previousUser = queryClient.getQueryData(['user', userData?.id]);
      queryClient.setQueryData(['user', userData?.id], (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          wallpaper_item_id: nextWallpaperItemId,
        };
      });

      return { previousUser };
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['user'] });
    },
    onError: (error, _variables, context) => {
      if (context?.previousUser) {
        queryClient.setQueryData(['user', userData?.id], context.previousUser);
      }
      setApplyError(error?.message || t('shopPage.updateFailed'));
    },
  });

  return (
    <>
      <main className="main-content shop-page">
        <h1 className="page-title">{t('shopPage.title')}</h1>

        <section className="shop-hero">
        <div className="shop-hero-copy">
          <span className="shop-kicker">{t('shopPage.kicker')}</span>
          <h1 className="shop-title">{t('shopPage.heroTitle')}</h1>
          <p className="shop-lead">
            {t('shopPage.heroLead')}
          </p>

          <div className="shop-balance-row">
            <div className="shop-balance-card">
              <span className="shop-balance-label">{t('shopPage.currentCoins')}</span>
              <strong className="shop-balance-value">{coins}</strong>
            </div>
            <div className="shop-balance-card shop-balance-card-soft">
              <span className="shop-balance-label">{t('shopPage.activeCategories')}</span>
              <strong className="shop-balance-value">{itemTypes.length}</strong>
            </div>
          </div>
        </div>

        <div className="shop-hero-panel">
          <div className="shop-hero-panel-head">
            <span className="shop-panel-tag">{t('shopPage.catalogStatus')}</span>
            <span className="shop-panel-season">{isLoading ? t('common.loading') : t('shopPage.itemsCount', { count: shopItems.length })}</span>
          </div>
          <p className="shop-hero-panel-copy">
            {t('shopPage.catalogLead')}
          </p>
          <div className="shop-filter-row" aria-label={t('shopPage.shopCategories')}>
            {itemTypes.length > 0 ? (
              itemTypes.map((type) => (
                <span key={type} className="shop-filter-chip">
                  {formatTypeLabel(type)}
                </span>
              ))
            ) : (
              <span className="shop-filter-chip">{t('shopPage.noCategories')}</span>
            )}
          </div>
        </div>
        </section>

        <section className="shop-content-grid">
        <div className="shop-catalog-shell">
          <div className="shop-section-head">
            <div>
              <span className="shop-section-kicker">{t('shopPage.catalog')}</span>
              <h2 className="shop-section-title">{t('shopPage.shopItems')}</h2>
            </div>
          </div>

          {purchaseError && (
            <div className="shop-feedback shop-feedback-error" role="status" aria-live="polite">
              {purchaseError}
            </div>
          )}

          {applyError && (
            <div className="shop-feedback shop-feedback-error" role="status" aria-live="polite">
              {applyError}
            </div>
          )}

          {isLoading ? (
            <div className="shop-empty-state">{t('shopPage.loadingItems')}</div>
          ) : itemsByType.length === 0 ? (
            <div className="shop-empty-state">{t('shopPage.noActiveItems')}</div>
          ) : (
            itemsByType.map((group) => (
              <section key={group.type} className="shop-group">
                <div className="shop-group-head">
                  <h3 className="shop-group-title">{group.label}</h3>
                  <span className="shop-group-count">{t('shopPage.itemsCount', { count: group.items.length })}</span>
                </div>

                <div className="shop-catalog-grid">
                  {group.items.map((item) => {
                    const imageUrl = buildAssetUrl(baseUrl, item.path);
                    const isEquipped = wallpaperItemId !== null && Number(wallpaperItemId) === Number(item.id);
                    const isOwned = ownedItemIdSet.has(Number(item.id));
                    const isPurchasing = purchaseMutation.isPending && Number(purchaseMutation.variables) === Number(item.id);
                    const isApplying = applyMutation.isPending && Number(applyMutation.variables?.wallpaperItemId ?? -1) === Number(item.id);
                    const isUnequipping = applyMutation.isPending && Number(wallpaperItemId) === Number(item.id) && applyMutation.variables?.wallpaperItemId === null;
                    const actionLabel = !isOwned
                      ? isPurchasing
                        ? t('shopPage.purchasing')
                        : t('shopPage.purchase')
                      : isEquipped
                        ? isUnequipping
                          ? t('shopPage.updating')
                          : t('shopPage.equipped')
                        : isApplying
                          ? t('shopPage.applying')
                          : t('shopPage.apply');

                    return (
                      <article key={item.id} className="shop-item-card">
                        <div className="shop-item-art">
                          {imageUrl ? (
                            <img
                              className="shop-item-image"
                              src={imageUrl}
                              alt={item.name || item.code || t('shopPage.shopItemFallback', { id: item.id })}
                            />
                          ) : (
                            <div className="shop-item-image shop-item-image-fallback" aria-hidden="true" />
                          )}
                        </div>

                        <div className="shop-item-body">
                          <div className="shop-item-headline">
                            <h3 className="shop-item-name">{item.name || item.code || t('shopPage.itemFallback', { id: item.id })}</h3>
                            <span className="shop-item-price">{t('shopPage.coinsAmount', { count: item.price ?? 0 })}</span>
                          </div>

                          <div className="shop-item-meta">
                            <span className="shop-item-meta-chip">{group.label}</span>
                            {isEquipped && <span className="shop-item-meta-chip shop-item-meta-chip-active">{t('shopPage.equipped')}</span>}
                            {isOwned && <span className="shop-item-meta-chip shop-item-meta-chip-owned">{t('shopPage.owned')}</span>}
                          </div>

                          <div className="shop-item-footer">
                            <button
                              type="button"
                              className="shop-card-button shop-card-button-secondary"
                              onClick={() => setPreviewItem({
                                id: item.id,
                                name: item.name || item.code || t('shopPage.itemFallback', { id: item.id }),
                                imageUrl,
                              })}
                            >
                              {t('shopPage.preview')}
                            </button>
                            <button
                              type="button"
                              className={`shop-card-button shop-card-button-primary ${isOwned ? 'shop-card-button-owned-action' : ''} ${isEquipped ? 'shop-card-button-equipped' : ''}`}
                              onClick={() => {
                                if (!isOwned) {
                                  purchaseMutation.mutate(item.id);
                                  return;
                                }

                                if (!userData?.id) return;

                                applyMutation.mutate({
                                  userId: userData.id,
                                  wallpaperItemId: isEquipped ? null : item.id,
                                });
                              }}
                              disabled={isPurchasing || applyMutation.isPending}
                              title={isOwned && isEquipped ? t('shopPage.clickToUnequip') : ''}
                            >
                              {actionLabel}
                            </button>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </section>
            ))
          )}
        </div>

        <aside className="shop-sidebar-panel">
          <div className="shop-section-head shop-section-head-compact">
            <div>
              <span className="shop-section-kicker">{t('shopPage.overview')}</span>
              <h2 className="shop-section-title">{t('shopPage.yourShop')}</h2>
            </div>
          </div>

          <div className="shop-overview-stack">
            <div className="shop-overview-card">
              <span className="shop-overview-label">{t('shopPage.currentCoinsLower')}</span>
              <strong className="shop-overview-value">{coins}</strong>
            </div>
            <div className="shop-overview-card">
              <span className="shop-overview-label">{t('shopPage.categories')}</span>
              <strong className="shop-overview-value">{itemTypes.length}</strong>
            </div>
            <div className="shop-overview-card">
              <span className="shop-overview-label">{t('shopPage.equippedItem')}</span>
              <strong className="shop-overview-value">{equippedItemLabel}</strong>
            </div>
          </div>
        </aside>
        </section>
      </main>

      {previewItem && (
        <div
          className="shop-preview-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={t('shopPage.previewAria', { name: previewItem.name })}
          onClick={() => setPreviewItem(null)}
        >
          <div className="shop-preview-frame">
            <div className="shop-preview-shell" onClick={(event) => event.stopPropagation()}>
              <button
                type="button"
                className="shop-preview-close"
                onClick={() => setPreviewItem(null)}
                aria-label={t('shopPage.closePreview')}
              >
                {t('shopPage.close')}
              </button>

              <div className="shop-preview-stage">
                {previewItem.imageUrl ? (
                  <img
                    className="shop-preview-image"
                    src={previewItem.imageUrl}
                    alt={previewItem.name}
                  />
                ) : (
                  <div className="shop-preview-empty">{t('shopPage.previewUnavailable')}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
